import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usersApi, CreateUserData, UpdateUserData } from '../../api/usersApi';
import { departmentsApi } from '../../api/departmentsApi';
import { rolesApi } from '../../api/rolesApi';
import { User, Department, Role } from '../../types';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Card } from '../../components/common/Card';
import { Icons } from '../../components/common/Icons';
import { SortIcon } from '../../components/common/SortIcon';
import { useSorting, applySorting } from '../../hooks/useSorting';
import { TableFilters, ColumnConfig, FilterConfig } from '../../components/common/TableFilters';
import { isValidEmail, downloadBlob } from '../../utils/helpers';
import './Users.css';

interface Manager {
  id: number;
  name: string;
  email: string;
  employee_code: string;
  department: string | null;
}

// Column configuration
const ALL_COLUMNS: ColumnConfig[] = [
  { key: 'name', label: 'Name', defaultVisible: true },
  { key: 'email', label: 'Email', defaultVisible: true },
  { key: 'employee_code', label: 'Employee Code', defaultVisible: true },
  { key: 'department', label: 'Department', defaultVisible: true },
  { key: 'manager', label: 'Manager', defaultVisible: true },
  { key: 'job_position', label: 'Job Position', defaultVisible: false },
  { key: 'category', label: 'Category', defaultVisible: false },
  { key: 'roles', label: 'Roles', defaultVisible: true },
  { key: 'status', label: 'Status', defaultVisible: true },
];

// Map frontend column keys to backend export column keys
const EXPORT_COLUMN_MAP: Record<string, string> = {
  department: 'department_name',
  manager: 'manager_name',
  status: 'is_active',
};

const DEFAULT_VISIBLE_COLUMNS = ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key);

type UserSortKey = 'name' | 'email' | 'employee_code' | 'department' | 'manager' | 'job_position' | 'category' | 'status';


// Helper to read URL params (static function, no hooks)
function parseUrlFilters(params: URLSearchParams): Record<string, string | number | undefined> {
  const filters: Record<string, string | number | undefined> = { is_active: 'true' };
  const urlDeptId = params.get('department_id');
  const urlManagerId = params.get('manager_id');
  const urlRole = params.get('role');
  const urlIsActive = params.get('is_active');
  
  if (urlDeptId) filters.department_id = parseInt(urlDeptId, 10);
  if (urlManagerId) filters.manager_id = parseInt(urlManagerId, 10);
  if (urlRole) filters.role = urlRole;
  if (urlIsActive !== null) filters.is_active = urlIsActive;
  
  return filters;
}

export function Users() {
  const [searchParams] = useSearchParams();
  
  // Read URL params once on mount using a ref to capture initial values
  const initialFiltersRef = useRef<Record<string, string | number | undefined> | null>(null);
  if (initialFiltersRef.current === null) {
    initialFiltersRef.current = parseUrlFilters(searchParams);
  }
  
  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [exportLoading, setExportLoading] = useState(false);
  
  // Sorting state
  const { sortConfig, handleSort, getSortDirection, isColumnSorted } = useSorting<UserSortKey>();
  
  // Filter state - initialize from URL params captured in ref
  const [filterValues, setFilterValues] = useState<Record<string, string | number | undefined>>(
    () => initialFiltersRef.current || { is_active: 'true' }
  );
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_VISIBLE_COLUMNS);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const perPage = 50;


  const [formData, setFormData] = useState<CreateUserData>({
    email: '',
    name: '',
    employee_code: '',
    department_id: 0,
    job_position: '',
    manager_id: undefined,
    roles: ['employee'],
  });

  // Build filter configs from loaded data - memoize to prevent unnecessary re-renders
  const filterConfigs: FilterConfig[] = useMemo(() => [
    {
      key: 'department_id',
      label: 'Department',
      options: departments.map(d => ({ value: d.id, label: d.name })),
    },
    {
      key: 'role',
      label: 'Role',
      options: roles.map(r => ({ value: r.name, label: r.display_name })),
    },
    {
      key: 'manager_id',
      label: 'Manager',
      options: managers.map(m => ({ value: m.id, label: m.name })),
    },
    {
      key: 'is_active',
      label: 'Status',
      options: [
        { value: 'true', label: 'Active Only' },
        { value: 'false', label: 'Inactive Only' },
      ],
    },
    {
      key: 'category',
      label: 'Category',
      options: Array.from(new Set(users.map((u: any) => u.category).filter(Boolean))).map(c => ({ value: c as string, label: c as string })),
    },
    {
      key: 'practice',
      label: 'Practice',
      options: Array.from(new Set(users.map((u: any) => u.practice).filter(Boolean))).map(p => ({ value: p as string, label: p as string })),
    },
  ], [departments, roles, managers, users]);

  // Ref to prevent concurrent fetches
  const isFetchingRef = useRef(false);

  // Load initial data once on mount
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    
    const loadData = async () => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      
      try {
        setLoading(true);
        setError(null);
        const filters = initialFiltersRef.current || { is_active: 'true' };
        const [usersRes, deptsRes, rolesRes, managersRes] = await Promise.all([
          usersApi.getUsers({ 
            page: 1, 
            per_page: perPage, 
            search: '',
            department_id: filters.department_id as number | undefined,
            role: filters.role as string | undefined,
            manager_id: filters.manager_id as number | undefined,
            is_active: filters.is_active as string | undefined,
          }),
          departmentsApi.getDepartments(),
          rolesApi.getRoles(),
          usersApi.getManagers(),
        ]);
        setUsers(usersRes.users);
        setTotalPages(usersRes.pages || 1);
        setTotalUsers(usersRes.total || 0);
        setCurrentPage(1);
        setDepartments(deptsRes.departments);
        setRoles(rolesRes.roles);
        setManagers(managersRes.managers);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    };
    
    loadData();
  }, []);

  // Fetch users with current filters (for filter changes, pagination, search)
  const fetchUsers = useCallback(async (page: number, search: string, filters: Record<string, string | number | undefined>) => {
    // Prevent concurrent fetches
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    
    try {
      setLoading(true);
      setError(null);
      const usersRes = await usersApi.getUsers({ 
        page, 
        per_page: perPage, 
        search,
        department_id: filters.department_id as number | undefined,
        role: filters.role as string | undefined,
        manager_id: filters.manager_id as number | undefined,
        is_active: filters.is_active as string | undefined,
      });
      setUsers(usersRes.users);
      setTotalPages(usersRes.pages || 1);
      setTotalUsers(usersRes.total || 0);
      setCurrentPage(page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  const handleFilterChange = useCallback((key: string, value: string | number | undefined) => {
    const newFilters = { ...filterValues, [key]: value };
    setFilterValues(newFilters);
    fetchUsers(1, searchTerm, newFilters);
  }, [fetchUsers, searchTerm, filterValues]);

  const handleSearch = useCallback(async () => {
    await fetchUsers(1, searchTerm, filterValues);
  }, [fetchUsers, searchTerm, filterValues]);

  const handlePageChange = useCallback((newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchUsers(newPage, searchTerm, filterValues);
    }
  }, [fetchUsers, searchTerm, filterValues, totalPages]);

  const handleRefresh = useCallback(() => {
    fetchUsers(currentPage, searchTerm, filterValues);
  }, [fetchUsers, currentPage, searchTerm, filterValues]);

  const handleExport = async (format: 'csv' | 'xlsx') => {
    try {
      setExportLoading(true);
      // Map frontend column keys to backend export column keys
      const exportColumns = visibleColumns.map(col => EXPORT_COLUMN_MAP[col] || col);
      const blob = await usersApi.exportUsers({
        format,
        search: searchTerm || undefined,
        department_id: filterValues.department_id as number | undefined,
        role: filterValues.role as string | undefined,
        manager_id: filterValues.manager_id as number | undefined,
        is_active: filterValues.is_active as string | undefined,
        columns: exportColumns.join(','),
      });
      downloadBlob(blob, `employees.${format}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export');
    } finally {
      setExportLoading(false);
    }
  };

  const validateUserForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Name is required.';
    else if (formData.name.length > 60) errors.name = 'Name must be 60 characters or fewer.';
    if (formData.employee_code && formData.employee_code.length > 20) errors.employee_code = 'Employee code must be 20 characters or fewer.';
    if (formData.job_position && formData.job_position.length > 60) errors.job_position = 'Job position must be 60 characters or fewer.';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateUserForm()) return;
    // Validate email format
    if (!isValidEmail(formData.email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError(null);
    
    try {
      const res = await usersApi.createUser(formData);
      setTempPassword(res.temporary_password);
      setUsers([...users, res.user]);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    if (!validateUserForm()) return;
    // Validate email format
    if (!isValidEmail(formData.email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError(null);
    
    try {
      setLoading(true);
      const updateData: UpdateUserData = {
        name: formData.name,
        email: formData.email,
        employee_code: formData.employee_code,
        department_id: formData.department_id,
        job_position: formData.job_position,
        manager_id: formData.manager_id,
        roles: formData.roles,
      };
      const res = await usersApi.updateUser(editingUser.id, updateData);
      setUsers(users.map(u => u.id === editingUser.id ? res.user : u));
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;
    try {
      setLoading(true);
      await usersApi.deactivateUser(userId);
      setUsers(users.map(u => u.id === userId ? { ...u, is_active: false } : u));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate user');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (userId: number) => {
    if (!window.confirm('Are you sure you want to reset this user\'s password?')) return;
    try {
      setLoading(true);
      const res = await usersApi.resetPassword(userId);
      setTempPassword(res.temporary_password);
      alert(`Password reset! New password: ${res.temporary_password}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setFormErrors({});
    setEmailError(null);
    setFormData({
      email: user.email,
      name: user.name,
      employee_code: user.employee_code,
      department_id: user.department_id || 0,
      job_position: user.job_position || '',
      manager_id: user.manager_id || undefined,
      roles: user.roles,
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingUser(null);
    setEmailError(null);
    setFormErrors({});
    setFormData({
      email: '',
      name: '',
      employee_code: '',
      department_id: 0,
      job_position: '',
      manager_id: undefined,
      roles: ['employee'],
    });
  };

  // Apply sorting to users
  const sortedUsers = useMemo(() => {
    return applySorting(users, sortConfig, (user, key) => {
      switch (key) {
        case 'name': return user.name;
        case 'email': return user.email;
        case 'employee_code': return user.employee_code || '';
        case 'department': return user.department || '';
        case 'manager': return user.manager || '';
        case 'job_position': return user.job_position || '';
        case 'category': return (user as any).category || '';
        case 'status': return user.is_active ? 'Active' : 'Inactive';
        default: return '';
      }
    });
  }, [users, sortConfig]);

  const renderUserRow = (u: User, cols: string[]) => (
    <tr key={u.id}>
      {cols.includes('name') && (
        <td>
          <div className="user-info">
            <div className="user-avatar">{u.name.charAt(0).toUpperCase()}</div>
            <span className="user-name">{u.name}</span>
          </div>
        </td>
      )}
      {cols.includes('email') && <td>{u.email}</td>}
      {cols.includes('employee_code') && <td>{u.employee_code}</td>}
      {cols.includes('department') && <td>{u.department || '-'}</td>}
      {cols.includes('manager') && <td>{u.manager || '-'}</td>}
      {cols.includes('job_position') && <td>{u.job_position || '-'}</td>}
      {cols.includes('category') && <td>{u.category || '-'}</td>}
      {cols.includes('roles') && (
        <td>
          <div className="role-badges">
            {u.roles.map((r) => (
              <span key={r} className={`role-badge role-${r}`}>{r.charAt(0).toUpperCase() + r.slice(1)}</span>
            ))}
          </div>
        </td>
      )}
      {cols.includes('status') && (
        <td>
          <span className={`status-badge ${u.is_active ? 'active' : 'inactive'}`}>
            {u.is_active ? 'Active' : 'Inactive'}
          </span>
        </td>
      )}
      <td className="actions-cell">
        <div className="action-buttons">
          <button className="action-btn edit" onClick={() => openEditModal(u)} title="Edit"><Icons.Edit /></button>
          <button className="action-btn key" onClick={() => handleResetPassword(u.id)} title="Reset Password"><Icons.Key /></button>
          {u.is_active && (
            <button className="action-btn delete" onClick={() => handleDeactivateUser(u.id)} title="Deactivate"><Icons.Trash /></button>
          )}
        </div>
      </td>
    </tr>
  );

  return (
    <div className="users-page">
      <div className="users-header">
        <div className="users-title">
          <h1>User Management</h1>
          <p>Manage all system users</p>
        </div>
        <Button onClick={() => setShowModal(true)}>
          <Icons.Plus /> Add User
        </Button>
      </div>

      {error && (
        <div className="error-banner">
          <Icons.AlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {tempPassword && (
        <div className="success-banner">
          <Icons.Check />
          <span>Temporary password: <strong>{tempPassword}</strong></span>
          <button onClick={() => setTempPassword(null)}>&times;</button>
        </div>
      )}

      <Card>
        <TableFilters
          searchPlaceholder="Search employees..."
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          onSearch={handleSearch}
          filters={filterConfigs}
          filterValues={filterValues}
          onFilterChange={handleFilterChange}
          columns={ALL_COLUMNS}
          visibleColumns={visibleColumns}
          onColumnToggle={setVisibleColumns}
          onExport={handleExport}
          exportLoading={exportLoading}
          onRefresh={handleRefresh}
          refreshLoading={loading}
          totalCount={totalUsers}
          shownCount={users.length}
          countLabel="Employees"
        />

        <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  {visibleColumns.includes('name') && (
                    <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Name
                      <SortIcon direction={getSortDirection('name')} isActive={isColumnSorted('name')} />
                    </th>
                  )}
                  {visibleColumns.includes('email') && (
                    <th onClick={() => handleSort('email')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Email
                      <SortIcon direction={getSortDirection('email')} isActive={isColumnSorted('email')} />
                    </th>
                  )}
                  {visibleColumns.includes('employee_code') && (
                    <th onClick={() => handleSort('employee_code')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Employee Code
                      <SortIcon direction={getSortDirection('employee_code')} isActive={isColumnSorted('employee_code')} />
                    </th>
                  )}
                  {visibleColumns.includes('department') && (
                    <th onClick={() => handleSort('department')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Department
                      <SortIcon direction={getSortDirection('department')} isActive={isColumnSorted('department')} />
                    </th>
                  )}
                  {visibleColumns.includes('manager') && (
                    <th onClick={() => handleSort('manager')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Manager
                      <SortIcon direction={getSortDirection('manager')} isActive={isColumnSorted('manager')} />
                    </th>
                  )}
                  {visibleColumns.includes('job_position') && (
                    <th onClick={() => handleSort('job_position')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Job Position
                      <SortIcon direction={getSortDirection('job_position')} isActive={isColumnSorted('job_position')} />
                    </th>
                  )}
                  {visibleColumns.includes('category') && (
                    <th onClick={() => handleSort('category')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Category
                      <SortIcon direction={getSortDirection('category')} isActive={isColumnSorted('category')} />
                    </th>
                  )}
                  {visibleColumns.includes('roles') && <th>Roles</th>}
                  {visibleColumns.includes('status') && (
                    <th onClick={() => handleSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Status
                      <SortIcon direction={getSortDirection('status')} isActive={isColumnSorted('status')} />
                    </th>
                  )}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map(u => renderUserRow(u, visibleColumns))}
              </tbody>
            </table>
        </div>

        {/* Pagination */}
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {users.length > 0 ? ((currentPage - 1) * perPage + 1) : 0} to {Math.min(currentPage * perPage, totalUsers)} of {totalUsers} users
          </div>
          <div className="pagination-controls">
            <button 
              className="pagination-btn" 
              onClick={() => handlePageChange(1)} 
              disabled={currentPage === 1 || loading}
              title="First page"
            >
              <Icons.ChevronsLeft />
            </button>
            <button 
              className="pagination-btn" 
              onClick={() => handlePageChange(currentPage - 1)} 
              disabled={currentPage === 1 || loading}
              title="Previous page"
            >
              <Icons.ChevronLeft />
            </button>
            
            <div className="pagination-pages">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    className={`pagination-btn page-num ${currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => handlePageChange(pageNum)}
                    disabled={loading}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button 
              className="pagination-btn" 
              onClick={() => handlePageChange(currentPage + 1)} 
              disabled={currentPage === totalPages || loading}
              title="Next page"
            >
              <Icons.ChevronRight />
            </button>
            <button 
              className="pagination-btn" 
              onClick={() => handlePageChange(totalPages)} 
              disabled={currentPage === totalPages || loading}
              title="Last page"
            >
              <Icons.ChevronsRight />
            </button>
          </div>
        </div>
      </Card>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Edit User' : 'Create User'}</h2>
              <button className="modal-close" onClick={resetForm}>&times;</button>
            </div>
            <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
              <div className="modal-body">
                <Input
                  label="Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  maxLength={60}
                  error={formErrors.name}
                  required
                />
                <Input
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    if (emailError) setEmailError(null);
                  }}
                  error={emailError || undefined}
                  required
                />
                <Input
                  label="Employee Code"
                  value={formData.employee_code}
                  onChange={(e) => setFormData({ ...formData, employee_code: e.target.value })}
                  maxLength={20}
                  error={formErrors.employee_code}
                  required
                />
                <div className="form-group">
                  <label>Department</label>
                  <select
                    value={formData.department_id}
                    onChange={(e) => setFormData({ ...formData, department_id: Number(e.target.value) })}
                    required
                  >
                    <option value="">Select Department</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                </div>
                <Input
                  label="Job Position"
                  value={formData.job_position || ''}
                  onChange={(e) => setFormData({ ...formData, job_position: e.target.value })}
                  maxLength={60}
                  error={formErrors.job_position}
                />
                <div className="form-group">
                  <label>Manager <span className="required">*</span></label>
                  <select
                    value={formData.manager_id || ''}
                    onChange={(e) => setFormData({ ...formData, manager_id: e.target.value ? Number(e.target.value) : undefined })}
                    required
                  >
                    <option value="">Select Manager</option>
                    {managers
                      .filter(m => m.id !== editingUser?.id) // Don't allow setting self as manager
                      .map((mgr) => (
                        <option key={mgr.id} value={mgr.id}>
                          {mgr.name} ({mgr.employee_code})
                        </option>
                      ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <select
                    value={formData.roles?.[0] || 'employee'}
                    onChange={(e) => setFormData({ ...formData, roles: [e.target.value] })}
                  >
                    {roles.map((role) => (
                      <option key={role.id} value={role.name}>{role.display_name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <Button type="button" variant="ghost" onClick={resetForm}>Cancel</Button>
                <Button type="submit" isLoading={loading}>
                  {editingUser ? 'Update User' : 'Create User'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
